/**
 * 记忆管理工具函数
 */

import type { MemoryEntry, MemoryCategory, SearchResult } from '../types'

/**
 * 计算两个文本的相似度（简单实现）
 * 实际项目中应该使用更复杂的算法如 Levenshtein 距离
 */
export function calculateTextSimilarity(text1: string, text2: string): number {
  const words1 = new Set(text1.toLowerCase().split(/\s+/))
  const words2 = new Set(text2.toLowerCase().split(/\s+/))
  
  const intersection = new Set([...words1].filter(x => words2.has(x)))
  const union = new Set([...words1, ...words2])
  
  return union.size > 0 ? intersection.size / union.size : 0
}

/**
 * 按相关性排序搜索结果
 */
export function sortByRelevance(results: SearchResult[]): SearchResult[] {
  return [...results].sort((a, b) => b.score - a.score)
}

/**
 * 按时间排序记忆
 */
export function sortByTime(
  memories: MemoryEntry[],
  order: 'asc' | 'desc' = 'desc'
): MemoryEntry[] {
  return [...memories].sort((a, b) => {
    const timeA = a.updatedAt.getTime()
    const timeB = b.updatedAt.getTime()
    return order === 'desc' ? timeB - timeA : timeA - timeB
  })
}

/**
 * 按重要性排序记忆
 */
export function sortByImportance(memories: MemoryEntry[]): MemoryEntry[] {
  return [...memories].sort((a, b) => 
    (b.metadata.importance || 0) - (a.metadata.importance || 0)
  )
}

/**
 * 过滤过期的记忆
 */
export function filterExpiredMemories(memories: MemoryEntry[]): MemoryEntry[] {
  const now = Date.now()
  
  return memories.filter(memory => {
    if (!memory.metadata.ttl) return true
    
    const expiryTime = memory.createdAt.getTime() + (memory.metadata.ttl * 1000)
    return expiryTime > now
  })
}

/**
 * 按分类分组记忆
 */
export function groupByCategory(
  memories: MemoryEntry[]
): Record<MemoryCategory, MemoryEntry[]> {
  const grouped: Partial<Record<MemoryCategory, MemoryEntry[]>> = {}
  
  memories.forEach(memory => {
    const category = memory.metadata.category || 'other'
    if (!grouped[category]) {
      grouped[category] = []
    }
    grouped[category]!.push(memory)
  })
  
  return grouped as Record<MemoryCategory, MemoryEntry[]>
}

/**
 * 按标签分组记忆
 */
export function groupByTags(memories: MemoryEntry[]): Record<string, MemoryEntry[]> {
  const grouped: Record<string, MemoryEntry[]> = {}
  
  memories.forEach(memory => {
    memory.metadata.tags?.forEach(tag => {
      if (!grouped[tag]) {
        grouped[tag] = []
      }
      grouped[tag].push(memory)
    })
  })
  
  return grouped
}

/**
 * 提取记忆的关键词
 */
export function extractKeywords(content: string, limit: number = 5): string[] {
  // 简单的关键词提取：按词频排序
  const words = content.toLowerCase()
    .replace(/[^\w\s\u4e00-\u9fa5]/g, '') // 保留中文、英文和空格
    .split(/\s+/)
    .filter(word => word.length > 2) // 过滤短词
  
  const wordCount: Record<string, number> = {}
  words.forEach(word => {
    wordCount[word] = (wordCount[word] || 0) + 1
  })
  
  return Object.entries(wordCount)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([word]) => word)
}

/**
 * 合并相似的记忆
 */
export function mergeSimilarMemories(
  memories: MemoryEntry[],
  similarityThreshold: number = 0.8
): MemoryEntry[] {
  const merged: MemoryEntry[] = []
  const processed = new Set<string>()
  
  memories.forEach(memory => {
    if (processed.has(memory.id)) return
    
    // 查找相似的记忆
    const similar = memories.filter(other => {
      if (other.id === memory.id || processed.has(other.id)) return false
      
      const similarity = calculateTextSimilarity(memory.content, other.content)
      return similarity >= similarityThreshold
    })
    
    if (similar.length > 0) {
      // 合并相似记忆
      const allMemories = [memory, ...similar]
      const mergedMemory: MemoryEntry = {
        ...memory,
        content: allMemories.map(m => m.content).join('\n---\n'),
        metadata: {
          ...memory.metadata,
          tags: Array.from(new Set(
            allMemories.flatMap(m => m.metadata.tags || [])
          )),
          importance: Math.max(
            ...allMemories.map(m => m.metadata.importance || 0)
          ),
          accessCount: allMemories.reduce(
            (sum, m) => sum + (m.metadata.accessCount || 0), 0
          )
        },
        updatedAt: new Date()
      }
      
      merged.push(mergedMemory)
      allMemories.forEach(m => processed.add(m.id))
    } else {
      merged.push(memory)
      processed.add(memory.id)
    }
  })
  
  return merged
}

/**
 * 计算记忆的衰减分数
 */
export function calculateDecayScore(
  memory: MemoryEntry,
  decayRate: number = 0.1
): number {
  const daysSinceAccess = memory.metadata.lastAccessedAt
    ? (Date.now() - memory.metadata.lastAccessedAt.getTime()) / (1000 * 60 * 60 * 24)
    : (Date.now() - memory.updatedAt.getTime()) / (1000 * 60 * 60 * 24)
  
  const baseScore = memory.metadata.importance || 0.5
  const accessBonus = Math.min((memory.metadata.accessCount || 0) * 0.01, 0.3)
  
  // 应用衰减
  const decayFactor = Math.exp(-decayRate * daysSinceAccess)
  
  return Math.max(0, Math.min(1, (baseScore + accessBonus) * decayFactor))
}

/**
 * 格式化记忆为可读文本
 */
export function formatMemoryAsText(memory: MemoryEntry): string {
  const lines = [
    `【${memory.metadata.category || '未分类'}】`,
    memory.content,
    `标签: ${memory.metadata.tags?.join(', ') || '无'}`,
    `重要性: ${memory.metadata.importance || 0.5}`,
    `创建时间: ${memory.createdAt.toLocaleString('zh-CN')}`,
    `更新时间: ${memory.updatedAt.toLocaleString('zh-CN')}`
  ]
  
  if (memory.metadata.accessCount) {
    lines.push(`访问次数: ${memory.metadata.accessCount}`)
  }
  
  return lines.join('\n')
}

/**
 * 生成记忆摘要
 */
export function generateMemorySummary(memories: MemoryEntry[]): string {
  const byCategory = groupByCategory(memories)
  const summary: string[] = [
    `记忆总数: ${memories.length}`,
    '',
    '按分类统计:'
  ]
  
  Object.entries(byCategory).forEach(([category, items]) => {
    if (items.length > 0) {
      summary.push(`- ${category}: ${items.length} 条`)
    }
  })
  
  // 找出最重要的记忆
  const topMemories = sortByImportance(memories).slice(0, 3)
  if (topMemories.length > 0) {
    summary.push('', '重要记忆:')
    topMemories.forEach((memory, index) => {
      const preview = memory.content.slice(0, 50) + 
        (memory.content.length > 50 ? '...' : '')
      summary.push(`${index + 1}. ${preview}`)
    })
  }
  
  return summary.join('\n')
}

/**
 * 验证记忆内容
 */
export function validateMemoryContent(content: string): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []
  
  if (!content || content.trim().length === 0) {
    errors.push('记忆内容不能为空')
  }
  
  if (content.length > 10000) {
    errors.push('记忆内容过长（最大 10000 字符）')
  }
  
  // 检查是否包含敏感信息（简单示例）
  const sensitivePatterns = [
    /password\s*[:=]\s*\S+/i,
    /api[_-]?key\s*[:=]\s*\S+/i,
    /secret\s*[:=]\s*\S+/i,
    /token\s*[:=]\s*\S+/i
  ]
  
  sensitivePatterns.forEach(pattern => {
    if (pattern.test(content)) {
      errors.push('记忆内容可能包含敏感信息')
    }
  })
  
  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * 清理记忆内容
 */
export function sanitizeMemoryContent(content: string): string {
  // 移除多余的空白
  let cleaned = content.trim().replace(/\s+/g, ' ')
  
  // 移除潜在的敏感信息
  cleaned = cleaned.replace(/password\s*[:=]\s*\S+/gi, 'password: [REDACTED]')
  cleaned = cleaned.replace(/api[_-]?key\s*[:=]\s*\S+/gi, 'api_key: [REDACTED]')
  cleaned = cleaned.replace(/secret\s*[:=]\s*\S+/gi, 'secret: [REDACTED]')
  cleaned = cleaned.replace(/token\s*[:=]\s*\S+/gi, 'token: [REDACTED]')
  
  return cleaned
}

/**
 * 估算记忆存储大小
 */
export function estimateMemorySize(memory: MemoryEntry): number {
  // 简单估算：内容 + 元数据
  const contentSize = new Blob([memory.content]).size
  const metadataSize = new Blob([JSON.stringify(memory.metadata)]).size
  const embeddingSize = memory.embedding ? memory.embedding.length * 4 : 0 // float32
  
  return contentSize + metadataSize + embeddingSize
}

/**
 * 批量估算记忆存储大小
 */
export function estimateTotalMemorySize(memories: MemoryEntry[]): {
  totalSize: number
  averageSize: number
  humanReadable: string
} {
  const totalSize = memories.reduce((sum, memory) => 
    sum + estimateMemorySize(memory), 0
  )
  
  const averageSize = memories.length > 0 ? totalSize / memories.length : 0
  
  const humanReadable = formatBytes(totalSize)
  
  return { totalSize, averageSize, humanReadable }
}

/**
 * 格式化字节数为人类可读格式
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}